import React, { useContext, useState, useEffect } from "react";
import { Save, RotateCcw, Crosshair, MinusCircle, PlusCircle, Sparkles } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { motion, AnimatePresence } from "motion/react";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import BryntumContext, { userPreferences } from "../../context/bryntumContext.jsx";
import useViews from "../../hooks/useViews.jsx";
import { VIEW_TYPES } from "../../../other/constants/OtherKeys.jsx";
import { canSaveView, resetSubGrids } from "../../../other/utils/utils.jsx";
import { MessageDialog, StringHelper } from "@bryntum/schedulerpro";
import ViewStateBadges from "./ViewStateBadges.jsx";
import ViewMoreMenu from "./ViewMoreMenu.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { getScheduleInstanceFromViewManager } from "../../../other/utils/otherUtils.jsx";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { AppSearchBar } from "./AppSearchBar";
import { SwivuSolChatBox } from "@/modern/components/chatbox";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";

const ViewSpecificHeader = () => {
  const { viewManager, user, selectedBoard, currentViewRef } = useContext(BryntumContext);
  const viewManagerState = useViews();
  useSwivuStore({ storeId: STORE_KEYS.userPreferencesStore, subscriberFn: ({ record: changedRecord }) => changedRecord?.key === PERSISTENCE_STATE_KEYS.viewStatePIITS });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatSessionId, setChatSessionId] = useState();
  const [chatBoardId, setChatBoardId] = useState();
  const [chatBoardViewId, setChatBoardViewId] = useState();
  const [isChatFullScreen, setIsChatFullScreen] = useState(false);

  // Update board and view IDs when they change (while chat is open)
  useEffect(() => {
    if (isChatOpen) {
      // Update board ID
      const board = getSavedBoardSelection();
      if (board?.id) {
        setChatBoardId(board.id);
      }
      // Update view ID
      if (viewManagerState?.selectedView?.id) {
        setChatBoardViewId(viewManagerState.selectedView.id);
      }
    }
  }, [isChatOpen, selectedBoard?.id, viewManagerState?.selectedView?.id]);

  if (!viewManagerState?.selectedView || !user || !selectedBoard) {
    return null;
  }

  const { selectedView } = viewManagerState;
  const isSchedulerView = [VIEW_TYPES.schedulerView, VIEW_TYPES.planView].includes(selectedView.viewType);
  const isButtonReadOnly = !user.can(GRANTS.grant_four);
  const canShowSaveButton = selectedView.isOwnView;
  const isSaveButtonDisabled = !viewManagerState.selectedView.hasPIITS; /* Only enable button if the view has PIITS */

  const handleSaveView = () => {
    if (canSaveView(selectedView.viewType)) {
      const isViewUpdated = viewManager.updateView(selectedView.id, selectedView);
      if (isViewUpdated) {
        MessageDialog.alert({
          title: "Success",
          message: "View updated successfully."
        });
      } else {
        MessageDialog.alert({
          title: "Failed",
          message: "Unable to update the view."
        });
      }
    }
  };

  const onMoveTimelineToCurrentDate = () => {
    const reDistributePrefSetting = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.redistributeSubGridPanels, true));
    if (reDistributePrefSetting && viewManager.pageViewsRef) {
      resetSubGrids(Object.values(viewManager.pageViewsRef));
    }
    currentViewRef.current.scrollSchedulerTimeAxisToCurrDate();
  };

  const onTickSizeHandler = (value) => {
    const schedulerView = getScheduleInstanceFromViewManager(viewManager);
    if (schedulerView) {
      const tickSizeLimit = { min: 50, max: 800 };
      const tickSize = schedulerView?.tickSize + value;
      if (tickSize < tickSizeLimit.min) {
        schedulerView.tickSize = tickSizeLimit.min;
      } else if (tickSize > tickSizeLimit.max) {
        schedulerView.tickSize = tickSizeLimit.max;
      } else if (tickSize >= tickSizeLimit.min && tickSize <= tickSizeLimit.max) {
        schedulerView.tickSize = tickSize;
        if (!currentViewRef.current.swivuLastViewportCenterDate) {
          currentViewRef.current.swivuLastViewportCenterDate = schedulerView.viewportCenterDate;
        }
        if (currentViewRef.current.swivuLastViewportCenterDate) {
          schedulerView.scrollToDate(currentViewRef.current.swivuLastViewportCenterDate, { block: "center" });
        }
      }
    }
  };

  const handleTidyUpColumns = () => {
    if (viewManager.pageViewsRef) {
      resetSubGrids(Object.values(viewManager.pageViewsRef));
    }
  };

  const handleOpenChat = () => {
    // Get current board and view context when opening chat
    try {
      const board = getSavedBoardSelection();
      if (board?.id) {
        setChatBoardId(board.id);
      }
      if (viewManagerState?.selectedView?.id) {
        setChatBoardViewId(viewManagerState.selectedView.id);
      }
    } catch (error) {
      console.error("Failed to get board/view context:", error);
    }
    setIsChatOpen(true);
  };

  return (
    <div className="relative flex justify-between items-center min-h-12 px-5 bg-accent border-0 border-b border-b-border border-solid gap-4 text-foreground">
      {/* Left Side: View name and state badges */}
      <div className="flex items-center gap-8 text-foreground">
        {/* Search Bar */}
        <motion.div layout className="flex items-center gap-4">
          <AppSearchBar />
          {/* View State Badges */}
          <ViewStateBadges />
        </motion.div>
      </div>

      {/* Right Side: Search, controls, and actions */}
      <div className="flex items-center gap-2">
        <ButtonGroup>
          {/* Timeline Controls for Scheduler Views */}
          {isSchedulerView && (
            <>
              <ButtonGroup>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon-sm" disabled={isButtonReadOnly} onClick={onMoveTimelineToCurrentDate}>
                      <Crosshair />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Focus Timeline</TooltipContent>
                </Tooltip>

                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon-sm" disabled={isButtonReadOnly} onClick={() => onTickSizeHandler(-50)}>
                      <MinusCircle />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Decrease Column Width</TooltipContent>
                </Tooltip>

                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon-sm" disabled={isButtonReadOnly} onClick={() => onTickSizeHandler(50)}>
                      <PlusCircle />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Increase Column Width</TooltipContent>
                </Tooltip>
              </ButtonGroup>
            </>
          )}
          <ButtonGroup>
            {/* Reset/Tidy Up Columns Button */}
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={handleTidyUpColumns} disabled={isButtonReadOnly}>
                  <RotateCcw />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset Columns</TooltipContent>
            </Tooltip>

            {/* Save View Button - Icon only, always visible */}
            {canShowSaveButton && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon-sm" onClick={handleSaveView} disabled={isSaveButtonDisabled}>
                    <Save />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Save View</TooltipContent>
              </Tooltip>
            )}

            {/* Swivu Sol AI Assistant Button */}
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={handleOpenChat}>
                  <Sparkles className="text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-1">
                  <span>Ask Swivu Sol AI</span>
                  <span className="text-[10px] text-amber-500">⚠️ Experimental Feature</span>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* More Menu */}
            <ViewMoreMenu />
          </ButtonGroup>
        </ButtonGroup>
      </div>

      {/* Floating Chat Window */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className={
              isChatFullScreen
                ? "fixed inset-4 z-[9998] shadow-2xl rounded-lg overflow-hidden"
                : "fixed bottom-4 right-4 z-[9998] w-[500px] h-[650px] max-h-[calc(100vh-2rem)] shadow-2xl rounded-lg overflow-hidden"
            }
          >
            <div className="relative h-full">
              {/* Header buttons */}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                {/* Full screen toggle */}
                <button
                  onClick={() => setIsChatFullScreen(!isChatFullScreen)}
                  className="size-8 flex items-center justify-center rounded-full bg-background/80 hover:bg-background backdrop-blur-sm border border-border shadow-sm transition-colors"
                  aria-label={isChatFullScreen ? "Exit full screen" : "Full screen"}
                  title={isChatFullScreen ? "Exit full screen" : "Full screen"}
                >
                  <i className={`text-sm text-foreground ${isChatFullScreen ? "fa-solid fa-compress" : "fa-solid fa-expand"}`} />
                </button>
                {/* Close button */}
                <button
                  onClick={() => {
                    setIsChatOpen(false);
                    setIsChatFullScreen(false);
                  }}
                  className="size-8 flex items-center justify-center rounded-full bg-background/80 hover:bg-background backdrop-blur-sm border border-border shadow-sm transition-colors"
                  aria-label="Close chat"
                >
                  <i className="fa-solid fa-times text-sm text-foreground" />
                </button>
              </div>
              <SwivuSolChatBox
                welcomeMessage="Hello! I'm Swivu Sol, your AI assistant. I can help you with tasks, scheduling, projects, and more!&#10;&#10;⚠️ **Experimental Feature Notice**&#10;&#10;This AI assistant is currently in active development and testing. You may experience:&#10;- Slower response times than expected&#10;- Occasional inaccurate or incomplete information&#10; or Unexpected behavior&#10;&#10;We're working hard to improve the experience. Your feedback helps us make Swivu Sol better!"
                boardId={chatBoardId}
                boardViewId={chatBoardViewId}
                sessionId={chatSessionId}
                onSessionChange={setChatSessionId}
                currentUser={user}
                className="h-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ViewSpecificHeader;
